/*
 * Toasts: short notes that something happened. The server sends them with App\Classes\Toast
 * (masmerise/livewire-toaster carries them: a 'toaster:received' event on Livewire requests, the
 * session across a redirect); scripts call Toaster.success('…') and friends.
 *
 * The stack (resources/views/vendor/toaster/hub.blade.php) is a popover, so it sits in the
 * browser's top layer, above open dialogs too, and only the toasts themselves take clicks.
 */

import {AFTER_RELOAD} from './errors.js';

const EVENT = 'toaster:received';
const TYPES = ['info', 'success', 'warning', 'error'];
const MAX = 3;

const send = (type) => (message) => window.dispatchEvent(new CustomEvent(EVENT, {detail: {type, message}}));

window.Toaster = Object.fromEntries(TYPES.map((type) => [type, send(type)]));

document.addEventListener('alpine:init', () => {
    Alpine.data('toasts', (initial = [], duration = 5000) => ({
        toasts: [],
        nextId: 0,

        init() {
            // Where the stack lives on the page (it inherits --top-offset there)
            this.home = this.$root.parentElement;

            // Livewire dispatches on window, older callers on document: catch each event once
            const seen = new WeakSet();
            const receive = (event) => {
                if (!seen.has(event)) {
                    seen.add(event);
                    this.add(event.detail);
                }
            };

            window.addEventListener(EVENT, receive);
            document.addEventListener(EVENT, receive);

            // A closing dialog hands the stack back to the page
            document.addEventListener('close', (event) => {
                if (event.target.contains(this.$root)) {
                    this.home.append(this.$root);
                    this.toasts.length && this.raise();
                }
            }, true);

            initial.forEach((toast) => this.add(toast));

            // A note kept over a page swap (errors.js: a page opened afresh because it changed elsewhere)
            try {
                const kept = sessionStorage.getItem(AFTER_RELOAD);
                sessionStorage.removeItem(AFTER_RELOAD);
                kept && this.add(JSON.parse(kept));
            } catch {}
        },

        add({message, type = 'info'}) {
            // An Auto-Save's "saved" stays quiet: its Save button says so (unsaved.js); errors still show
            if (!message || (type === 'success' && window.unsaved?.auto)) {
                return;
            }

            type = TYPES.includes(type) ? type : 'info';

            // The same message again just restarts its timer
            const same = this.toasts.find((toast) => toast.message === message && toast.type === type);
            if (same) {
                return this.start(same);
            }

            const toast = {id: ++this.nextId, message, type, visible: true, timer: null};
            this.toasts.unshift(toast);
            this.toasts.slice(MAX).forEach((old) => this.dismiss(old));
            this.start(toast);
            this.raise();
        },

        start(toast) {
            clearTimeout(toast.timer);
            // Errors stay longer: they usually need reading
            toast.timer = setTimeout(() => this.dismiss(toast), toast.type === 'error' ? duration * 1.6 : duration);
        },

        pause(toast) {
            clearTimeout(toast.timer);
        },

        dismiss(toast) {
            clearTimeout(toast.timer);
            toast.visible = false;
            setTimeout(() => {
                this.toasts = this.toasts.filter((other) => other.id !== toast.id);
                if (!this.toasts.length) {
                    this.$root.hidePopover?.();
                }
            }, 250);
        },

        /*
         * Re-showing the popover puts it above anything opened since. An open modal dialog makes
         * the rest of the page inert (unclickable), so the stack moves inside it meanwhile.
         */
        raise() {
            const hub = this.$root;
            const modal = [...document.querySelectorAll('dialog[open]')].filter((dialog) => dialog.matches(':modal')).pop();
            const home = modal ?? this.home;

            if (hub.parentElement !== home) {
                home.append(hub);
            }

            if (hub.showPopover) {
                if (hub.matches(':popover-open')) {
                    hub.hidePopover();
                }
                hub.showPopover();
            }
        },
    }));
});
